import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Body, MenuPath, Note, SectionDivider, Warning } from '../ui';

import imgMenuPath from '../../../assets/images/docs/unreal-menu-path.png';
import imgToolImport from '../../../assets/images/docs/unreal-tool-import.png';
import imgToolExport from '../../../assets/images/docs/unreal-tool-export.png';
import imgEnablePlugins from '../../../assets/images/docs/unreal-enable-plugins.png';
import imgDmcGroups from '../../../assets/images/docs/unreal-dmc-groups.png';
import imgDmcLayers from '../../../assets/images/docs/unreal-dmc-layers.png';
import imgExportResult from '../../../assets/images/docs/unreal-export-result.png';
import imgOutputLog from '../../../assets/images/docs/unreal-output-log.png';

// ── Helpers ───────────────────────────────────────────────────────────────

// Shots are never upscaled: the dialog and menu captures are 1:1 on purpose, so
// their labels are not resampled. Anything wider than the column scales down to
// fit, anything narrower stays at native size and centres, which keeps the page
// on one axis instead of ragging between 546px and 860px. width/height are the
// files' real pixel sizes, passed so the browser reserves the space before the
// image loads rather than reflowing the text under it.
const Shot: React.FC<{
  src: string;
  alt: string;
  w: number;
  h: number;
}> = ({ src, alt, w, h }) => (
  <div className="my-[24px]">
    <img
      src={src}
      alt={alt}
      width={w}
      height={h}
      loading="lazy"
      decoding="async"
      className="block max-w-full w-auto h-auto mx-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
    />
  </div>
);

const Steps: React.FC<{ steps: React.ReactNode[] }> = ({ steps }) => (
  <ol className="p-0 mb-[8px] list-none">
    {steps.map((step, i) => (
      <li key={i} className="flex gap-[16px] mb-[16px] items-start">
        <span className="shrink-0 w-[28px] h-[28px] rounded-full bg-surface-hover border-[1.5px] border-border-brand flex items-center justify-center font-display text-[13px] font-bold text-blue-link">
          {i + 1}
        </span>
        <p className="mt-[4px] text-[14px] leading-[1.75] text-content-secondary">
          {step}
        </p>
      </li>
    ))}
  </ol>
);

// ── Page ──────────────────────────────────────────────────────────────────

const UnrealDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary">
      {/* -- Overview -- */}
      <h2
        id="overview"
        className="font-display text-[24px] font-extrabold text-content-primary mb-[16px] tracking-[-0.4px]"
      >
        {t('docs.unreal.title')}
      </h2>
      <Body>{t('docs.unreal.intro')}</Body>
      <Body>
        <Trans
          i18nKey="docs.unreal.intro2"
          components={{ strong: <strong /> }}
        />
      </Body>
      <Shot
        src={imgDmcLayers}
        alt={t('docs.unreal.dmcLayersAlt')}
        w={1280}
        h={466}
      />

      <SectionDivider />

      {/* -- Requirements -- */}
      <h3
        id="requirements"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.requirementsTitle')}
      </h3>
      <ul className="text-[14px] leading-[1.75] text-content-secondary mb-[16px] pl-[20px]">
        <li>
          <Trans
            i18nKey="docs.unreal.requirementEngine"
            components={{ strong: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.unreal.requirementPlatform"
            components={{ strong: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.unreal.requirementDmc"
            components={{ strong: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="docs.unreal.requirementSource"
            components={{ strong: <strong /> }}
          />
        </li>
      </ul>

      <SectionDivider />

      {/* -- Installing -- */}
      <h3
        id="installing"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.installingTitle')}
      </h3>
      <Steps
        steps={[
          <Trans
            i18nKey="docs.unreal.installStep1"
            components={{ code: <MenuPath>{''}</MenuPath> }}
          />,
          <Trans
            i18nKey="docs.unreal.installStep2"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.installStep3"
            components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
          />
        ]}
      />
      <Shot
        src={imgEnablePlugins}
        alt={t('docs.unreal.enablePluginsAlt')}
        w={922}
        h={166}
      />
      <Note label={t('docs.unreal.noteLabel')}>
        {t('docs.unreal.installNote')}
      </Note>

      <SectionDivider />

      {/* -- How the mapping works -- */}
      <h3
        id="mapping"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.mappingTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.unreal.mappingIntro"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Body>
        <Trans
          i18nKey="docs.unreal.mappingQuality"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Shot
        src={imgOutputLog}
        alt={t('docs.unreal.outputLogAlt')}
        w={860}
        h={178}
      />
      <Body>
        <Trans
          i18nKey="docs.unreal.mappingCache"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Note label={t('docs.unreal.noteLabel')}>
        <Trans
          i18nKey="docs.unreal.mappingNote"
          components={{ strong: <strong /> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Importing to DMC -- */}
      <h3
        id="import-dmc"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.importTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.unreal.importBody"
          components={{ code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Shot src={imgMenuPath} alt={t('docs.unreal.menuPathAlt')} w={700} h={108} />
      <Shot
        src={imgToolImport}
        alt={t('docs.unreal.toolImportAlt')}
        w={546}
        h={600}
      />
      <Steps
        steps={[
          <Trans
            i18nKey="docs.unreal.importStep1"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.importStep2"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.importStep3"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.importStep4"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.importStep5"
            components={{ strong: <strong /> }}
          />
        ]}
      />
      <Warning label={t('docs.unreal.warningLabel')}>
        <Trans
          i18nKey="docs.unreal.importWarning"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Warning>
      <Shot
        src={imgDmcGroups}
        alt={t('docs.unreal.dmcGroupsAlt')}
        w={1356}
        h={634}
      />
      <Note label={t('docs.unreal.noteLabel')}>
        <Trans
          i18nKey="docs.unreal.importNoneControl"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>

      <SectionDivider />

      {/* -- Exporting back to .touch -- */}
      <h3
        id="export-touch"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.exportTitle')}
      </h3>
      <Body>
        <Trans
          i18nKey="docs.unreal.exportBody"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Body>
      <Shot
        src={imgToolExport}
        alt={t('docs.unreal.toolExportAlt')}
        w={546}
        h={510}
      />
      <Steps
        steps={[
          <Trans
            i18nKey="docs.unreal.exportStep1"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.exportStep2"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.exportStep3"
            components={{ strong: <strong /> }}
          />,
          <Trans
            i18nKey="docs.unreal.exportStep4"
            components={{ strong: <strong /> }}
          />
        ]}
      />
      <Note label={t('docs.unreal.noteLabel')}>
        <Trans
          i18nKey="docs.unreal.exportNewSets"
          components={{ strong: <strong />, code: <MenuPath>{''}</MenuPath> }}
        />
      </Note>
      <Warning label={t('docs.unreal.warningLabel')}>
        {t('docs.unreal.exportWarning')}
      </Warning>
      <Shot
        src={imgExportResult}
        alt={t('docs.unreal.exportResultAlt')}
        w={1400}
        h={854}
      />

      <SectionDivider />

      {/* -- Troubleshooting -- */}
      <h3
        id="troubleshooting"
        className="font-display text-[17px] font-bold text-content-primary mb-[10px]"
      >
        {t('docs.unreal.troubleshootingTitle')}
      </h3>

      <h4 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.unreal.tsNoSourceTitle')}
      </h4>
      <Body>
        <Trans
          i18nKey="docs.unreal.tsNoSourceBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h4 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.unreal.tsNoChangeTitle')}
      </h4>
      <Body>
        <Trans
          i18nKey="docs.unreal.tsNoChangeBody"
          components={{ strong: <strong /> }}
        />
      </Body>

      <h4 className="font-display text-[15px] font-bold text-content-primary mt-[20px] mb-[6px]">
        {t('docs.unreal.tsStallTitle')}
      </h4>
      <Body>
        <Trans
          i18nKey="docs.unreal.tsStallBody"
          components={{ strong: <strong /> }}
        />
      </Body>
    </article>
  );
};

export default UnrealDoc;
